import { Component, Host, h, Element, State, Prop } from '@stencil/core';
import { handlePromoView } from '../../utils/promotionHelpers';
import { Size } from '../aha-pill/types';
import { FontSizing } from './types';

export interface AhaPromoTryPill extends HTMLAhaPromoTryPillElement {}

/**
 * A specific version of aha-pill used for promotions. Pill is always green and says "TRY".
 */
@Component({
  tag: 'aha-promo-try-pill',
  styleUrl: 'aha-promo-try-pill.css',
  shadow: true,
})
export class AhaPromoTryPill {
  @Element() host!: HTMLAhaPromoTryPillElement;

  /**
   * Set font size based on scale in getFontSize().
   */
  @Prop() fontSizing?: FontSizing = 'small';

  /**
   * Set size of aha-pill.
   */
  @Prop() pillSize?: Size = 'medium';

  /**
   * Set border color of aha-pill.
   */
  @Prop() borderColor?: string = 'transparent';

  /**
   * The snake case name of the upgrade location, found in SuiteHelper::ANALYTICS_TRACKING_PROPS.
   * The upgrade location is used to infer the promoted product, which then informs the tracking event details.
   *
   * Unlike our other promo components, this component does not require an upgrade location since it may
   * be used inside other promo components with their own analytics tracking.
   */
  @Prop() upgradeLocation?: string;

  @State() viewEventFired: boolean = false;

  private observer: any;
  componentDidLoad() {
    this.observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (!this.viewEventFired && this.upgradeLocation && entry.isIntersecting) {
            this.viewEventFired = true;
            handlePromoView(this.upgradeLocation);
          }
        });
      },
      { threshold: 0.5 },
    );

    this.observer.observe(this.host);
  }

  connectedCallback() {
    if (this.observer) {
      this.observer.observe(this.host);
    }
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  getFontSize() {
    switch (this.fontSizing) {
      case 'small':
        return '8px';
      case 'medium':
        return '10px';
      case 'large':
        return '12px';
      default:
        return '8px';
    }
  }

  render() {
    return (
      <Host>
        <aha-pill
          color="var(--theme-light-green-100-dark-green-600)"
          text-color="var(--theme-light-green-700-dark-green-500)"
          border-shape="round"
          size={this.pillSize}
          border-color={this.borderColor}
          style={{
            fontWeight: '400',
            fontSize: this.getFontSize(),
          }}
        >
          <span>TRY</span>
        </aha-pill>
      </Host>
    );
  }
}
